<!-- src/views/timesheets/ManagerTimesheetReviewView.vue -->
<template>
  <!-- 🛡️ CONTAINER WIDTH RESOLVED: Root wrappers aligned to match ClientAdminDashboardView.vue exactly -->
  <div class="p-4 sm:p-6 md:p-8">
    <div class="max-w-7xl mx-auto space-y-6 animate-fadeIn text-left">
      
      <!-- 1. DYNAMIC HEADER CARD -->
      <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 animate-fadeIn relative overflow-hidden text-left border-x border-t border-slate-800">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <ShieldCheckIcon class="h-32 w-32 text-white" />
        </div>
        <div class="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">
              {{ isFocusedMode ? 'Focused Record Review' : 'Authorisation Portal' }}
            </h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">
              {{ isFocusedMode ? 'Single Handshake Protocol Active' : 'Personnel Review • Command Hierarchy Active' }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <button v-if="isFocusedMode" @click="exitFocusedMode" class="bg-[#20FCF1] text-[#0A1630] px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2">
               <ListBulletIcon class="h-4 w-4" /> <span>View Full Queue</span>
            </button>
            <router-link :to="{ name: 'dashboard' }" class="bg-white/5 border border-white/10 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 shadow-lg active:scale-95">
               <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Dashboard</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- 2. SUCCESS FEEDBACK MODULE -->
      <transition name="fade-slide">
        <div v-if="successMessage" class="mb-8 p-6 bg-[#EBFDF8] border-2 border-[#20FCF1]/20 rounded-3xl shadow-xl flex items-center gap-6 animate-bounce">
           <div class="bg-[#20FCF1] p-3 rounded-2xl text-[#0A1630] shadow-lg">
             <CheckBadgeIcon class="h-6 w-6" />
           </div>
           <p class="text-sm font-black text-[#0A1630] uppercase tracking-widest">{{ successMessage }}</p>
        </div>
      </transition>

      <!-- 3. MAIN REGISTRY BODY -->
      <div class="pb-24">
        <div v-if="loading" class="flex flex-col items-center justify-center py-32">
          <div class="animate-spin h-12 w-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-6"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Hydrating Pending Records...</p>
        </div>

        <div v-else-if="submissions.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 shadow-inner animate-fadeIn">
           <ClockIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30 animate-pulse" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight text-center">Queue Complete</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2 text-center">No timesheets awaiting your approval.</p>
        </div>

        <div v-else class="space-y-6 animate-fadeIn relative z-0 text-left">
          <div v-for="sub in submissions" :key="sub.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl transition-all overflow-hidden group">
            
            <div class="p-6 md:p-8">
              <div class="flex flex-col xl:flex-row items-center justify-between gap-8">
                <div class="flex items-center gap-6 w-full xl:w-[35%] shrink-0 text-left">
                  <div class="relative h-16 w-16 shrink-0">
                    <img v-if="sub.userPhoto" :src="sub.userPhoto" class="h-16 w-16 rounded-2xl object-cover border border-slate-100 shadow-sm" alt="Personnel" />
                    <div v-else class="h-16 w-16 bg-[#0A1630] rounded-2xl flex items-center justify-center font-black text-[#20FCF1] text-xl border-b-2 border-[#20FCF1]">
                      {{ (getVal(sub, 'userName') || 'U').charAt(0) }}
                    </div>
                  </div>
                  <div class="min-w-0 flex flex-col text-left">
                    <div class="flex items-baseline gap-3 mb-1">
                      <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-none text-left">{{ getVal(sub, 'userName') }}</h3>
                    </div>
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="text-[9px] font-black text-[#F97316] uppercase tracking-widest bg-[#F97316]/10 px-2 py-0.5 rounded-md">Week {{ getVal(sub, 'railWeek') }}</span>
                      <span class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter truncate">{{ getVal(sub, 'department') }}</span>
                    </div>
                  </div>
                </div>

                <!-- Admin Actions -->
                <div class="flex flex-wrap items-center justify-center lg:justify-end gap-3 w-full xl:w-auto">
                  <button @click="toggleExpand(sub.id)" class="px-5 py-4 bg-slate-50 text-slate-500 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-[#0A1630] hover:text-white transition-all">
                    {{ expandedId === sub.id ? 'Hide Data' : 'Review Daily Log' }}
                  </button>

                  <button @click="initialiseQuery(sub)" class="px-5 py-4 bg-indigo-50 text-indigo-600 rounded-2xl text-[9px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all shadow-sm">
                    Raise Query
                  </button>

                  <button @click="processAuthorisation(sub, 'Authorised')" class="px-8 py-4 bg-[#20FCF1] text-[#0A1630] rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all">
                    Authorise Record
                  </button>

                  <button @click="initialiseDecline(sub)" title="Decline Entry" class="p-4 bg-rose-50 text-rose-500 rounded-2xl hover:bg-rose-500 hover:text-white transition-all shadow-sm">
                    <XMarkIcon class="h-5 w-5" />
                  </button>

                  <button v-if="userStore.isAdmin" @click="initialiseVoid(sub)" title="Void Record" class="p-4 bg-slate-100 text-slate-400 rounded-2xl hover:bg-rose-500 hover:text-white transition-all shadow-sm">
                    <ArchiveBoxXMarkIcon class="h-5 w-5" />
                  </button>
                </div>
              </div>

              <!-- 🛡️ AUDIT METADATA FOOTER -->
              <div class="mt-8 pt-6 border-t border-slate-50 flex flex-wrap items-center gap-6">
                <div class="flex items-center gap-2">
                  <ClockIcon class="h-3.5 w-3.5 text-slate-300" />
                  <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Handshake Initiated:</span>
                  <span class="text-[10px] font-bold text-[#0A1630] uppercase">{{ formatDate(getVal(sub, 'submittedAt')) }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <ShieldCheckIcon class="h-3.5 w-3.5 text-slate-300" />
                  <span class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Protocol Status:</span>
                  <span :class="statusColorClass(getVal(sub, 'status'))" class="text-[10px] font-black uppercase">{{ getVal(sub, 'status') }}</span>
                </div>
              </div>
            </div>

            <!-- 🛡️ EXPANDULAR LOG VIEW -->
            <Transition 
              enter-active-class="transition duration-300 ease-out" 
              enter-from-class="transform opacity-0 -translate-y-4" 
              enter-to-class="transform opacity-100 translate-y-0" 
              leave-active-class="transition duration-200 ease-in" 
              leave-from-class="transform opacity-100 translate-y-0" 
              leave-to-class="transform opacity-0 -translate-y-4"
            >
              <div v-if="expandedId === sub.id" class="bg-slate-50 p-6 sm:p-10 border-t border-slate-100 text-left">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-left">
                  <!-- SATURDAY - FRIDAY CARDS (7 ITEMS) -->
                  <div v-for="day in (getVal(sub, 'days') || [])" :key="day.isoDate" class="bg-white p-6 rounded-[32px] border border-slate-200 shadow-sm flex flex-col text-left min-w-0 transition-transform hover:scale-[1.02]">
                    <p class="text-[10px] font-black text-slate-400 uppercase mb-4 tracking-[0.2em] border-b border-slate-50 pb-3 text-left shrink-0">{{ day.dayName }}</p>
                    
                    <div v-if="!day.isOff && day.entries?.length" class="space-y-8 flex-grow text-left">
                      <div v-for="(entry, eIdx) in day.entries" :key="eIdx" class="border-l-4 border-[#F97316] pl-4 text-left">
                        <div class="flex flex-col gap-1.5 text-left mb-4">
                          <p class="text-3xl font-black text-[#0A1630] leading-none text-left">
                            {{ entry.hours }}<span class="text-xs text-slate-300 ml-1">H</span>
                          </p>
                          <p class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest break-all leading-tight">
                            {{ entry.jobNumber || entry.jobReference || entry.taskType || 'No Reference' }}
                          </p>
                        </div>
                        
                        <!-- 🛡️ SAFE DYNAMIC DAILY DEPT FALLBACK -->
                        <p class="text-[8px] font-bold text-slate-400 uppercase tracking-[0.15em] leading-none mb-4">
                          Department: {{ entry.department || getVal(sub, 'department') || 'General' }}
                        </p>

                        <div v-if="entry.description || entry.taskDescription" class="mt-4 pt-4 border-t border-slate-50">
                           <p class="text-[8px] font-black text-slate-300 uppercase tracking-[0.2em] mb-2">Task Description</p>
                           <p class="text-[10px] font-medium text-slate-600 leading-relaxed italic bg-slate-50/50 p-3 rounded-xl border border-slate-100 break-words shadow-inner">
                             "{{ entry.description || entry.taskDescription }}"
                           </p>
                        </div>
                      </div>
                    </div>
                    
                    <div v-else class="flex-grow flex items-center justify-center opacity-20 py-12"><MoonIcon class="h-8 w-8 text-slate-400" /></div>
                  </div>

                  <!-- SLOT 8: TOTAL PERIODIC HOURS -->
                  <div class="lg:col-span-1 flex flex-col justify-end">
                     <div class="bg-[#0A1630] p-8 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] text-center lg:text-right relative overflow-hidden h-full flex flex-col justify-center">
                        <div class="absolute -right-4 -bottom-4 opacity-5 pointer-events-none transform rotate-12">
                          <ClockIcon class="h-32 w-32 text-white" />
                        </div>
                        <p class="text-[11px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mb-3 relative z-10">Total Periodic Hours</p>
                        <p class="text-5xl font-black text-white leading-none relative z-10 tracking-tighter">
                          {{ getVal(sub, 'totalHours') }}<span class="text-lg text-slate-500 font-bold uppercase tracking-widest ml-2">HRS</span>
                        </p>
                     </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>

    <!-- MODALS (Query/Rejection) -->
    <div v-if="showQueryModal || showRejectionModal" class="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1630]/90 backdrop-blur-md px-4 animate-fadeIn">
        <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-lg p-10 border border-white/10 text-left">
          <div class="flex items-center gap-4 mb-8 text-left">
            <div :class="showQueryModal ? 'bg-indigo-600' : 'bg-rose-500'" class="text-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                <InformationCircleIcon v-if="showQueryModal" class="h-6 w-6" />
                <ExclamationTriangleIcon v-else class="h-6 w-6" />
            </div>
            <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">
              {{ showQueryModal ? 'Raise Protocol Query' : 'Decline Periodic Log' }}
            </h3>
          </div>
          <div class="space-y-6">
            <div class="p-5 bg-slate-50 rounded-2xl border border-slate-100 text-left">
                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 text-left">Personnel Identity</p>
                <p class="text-sm font-bold text-[#0A1630] uppercase text-left">{{ activeSubmission?.userName }} • Week {{ getVal(activeSubmission, 'railWeek') }}</p>
            </div>
            <div class="text-left">
                <label class="block text-[10px] font-black text-[#0A1630] uppercase tracking-widest mb-3 ml-1">Notes / Action Reason</label>
                <textarea v-model="actionNotes" rows="4" class="w-full border-slate-200 rounded-2xl p-5 text-sm font-bold bg-slate-50 outline-none focus:ring-2 transition-all shadow-inner" :class="showQueryModal ? 'focus:ring-indigo-500' : 'focus:ring-rose-500'" placeholder="Specify requirements..."></textarea>
            </div>
          </div>
          <div class="mt-10 flex gap-4">
            <button @click="closeModals" class="flex-1 py-5 text-[10px] font-black uppercase text-slate-400 text-center hover:text-[#0A1630] transition-colors">Abort</button>
            <button @click="showQueryModal ? confirmQuery() : confirmDecline()" :disabled="!actionNotes.trim() || isSubmittingAction" class="flex-1 py-5 rounded-2xl text-[10px] font-black uppercase shadow-xl disabled:opacity-50 text-white" :class="showQueryModal ? 'bg-indigo-600' : 'bg-rose-500'">
                {{ isSubmittingAction ? 'Synchronising...' : 'Confirm Action' }}
            </button>
          </div>
        </div>
    </div>

  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { db, auth } from '@/firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, serverTimestamp, addDoc, deleteDoc } from 'firebase/firestore';
import { 
  ShieldCheckIcon, XMarkIcon, ArrowUturnLeftIcon, 
  ClockIcon, MoonIcon, ExclamationTriangleIcon,
  InformationCircleIcon, ArchiveBoxXMarkIcon, ListBulletIcon, CheckBadgeIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

const submissions = ref([]);
const managersOnLeave = ref([]);
const loading = ref(true);
const expandedId = ref(null);
const successMessage = ref('');

const showRejectionModal = ref(false);
const showQueryModal = ref(false);
const actionNotes = ref('');
const activeSubmission = ref(null);
const isSubmittingAction = ref(false);

const isFocusedMode = computed(() => !!route.query.id);

const getVal = (sub, key) => {
    if (!sub) return null;
    return sub[key] !== undefined ? sub[key] : (sub.formData ? sub.formData[key] : null);
};

const formatDate = (ts) => {
  if (!ts) return 'N/A';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : (ts instanceof Date ? ts : new Date(ts));
  return d.toLocaleString('en-GB', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const statusColorClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('pending')) return 'text-amber-600';
  if (s.includes('authorised')) return 'text-[#20FCF1]';
  if (s.includes('declined')) return 'text-rose-500';
  if (s.includes('queried')) return 'text-indigo-600';
  return 'text-slate-500';
};

// 🛡️ LINTER GUARD: Localized ESLint bypass for template-level method detection
// eslint-disable-next-line no-unused-vars
const isFallback = (sub) => {
  const subDept = getVal(sub, 'department')?.toLowerCase().trim();
  return managersOnLeave.value.some(m => {
    const mDepts = (m.departments || []).map(d => d.toLowerCase().trim());
    if (m.department) mDepts.push(m.department.toLowerCase().trim());
    return mDepts.includes(subDept);
  });
};

// 🛡️ RE-ROUTED: Taken back to Financial Command Centre when exiting focus mode
const exitFocusedMode = () => {
    router.push({ name: 'AdminFinance' });
};

const purgeTimesheetNotification = async (submissionId) => {
  if (!auth.currentUser || !submissionId) return;
  try {
    const notificationsRef = collection(db, 'userProfiles', auth.currentUser.uid, 'notifications');
    const snap = await getDocs(notificationsRef);
    const snapsToPurge = [];
    snap.docs.forEach(d => {
      const data = d.data();
      const matches = data.relatedId === submissionId || 
                      data.submissionId === submissionId || 
                      data.timesheetId === submissionId || 
                      (data.link && data.link.includes(submissionId));
      if (matches) {
        snapsToPurge.push(d.ref);
      }
    });
    for (const ref of snapsToPurge) {
      await deleteDoc(ref);
    }

    const actionsRef = collection(db, 'user_actions');
    const qActions = query(
      actionsRef,
      where('status', '==', 'Pending')
    );
    const actionsSnap = await getDocs(qActions);
    const actionsToUpdate = [];
    actionsSnap.docs.forEach(d => {
      const data = d.data();
      const matches = data.relatedId === submissionId || 
                      data.submissionId === submissionId || 
                      data.timesheetId === submissionId || 
                      (data.link && data.link.includes(submissionId)) ||
                      (data.targetUserId === auth.currentUser.uid && (data.link && data.link.includes(submissionId)));
      if (matches) {
        actionsToUpdate.push(d.ref);
      }
    });

    for (const ref of actionsToUpdate) {
      await updateDoc(ref, {
        status: 'Completed',
        isRead: true,
        completedAt: serverTimestamp()
      });
    }
  } catch (err) {
    console.warn("Timesheet action/notification purge failed:", err);
  }
};

const fetchPendingLogs = async () => {
  loading.value = true;
  try {
    const profile = userStore.userProfile;
    const cid = userStore.clientId || profile?.clientId;
    if (!cid) return;

    if (isFocusedMode.value) {
        const docSnap = await getDoc(doc(db, 'timesheetSubmissions', route.query.id));
        if (docSnap.exists()) {
            const data = docSnap.data();
            const pSnap = await getDoc(doc(db, 'userProfiles', data.userId));
            const p = pSnap.exists() ? pSnap.data() : null;
            submissions.value = [{ 
                id: docSnap.id, 
                ...data, 
                userName: p ? `${p.firstName} ${p.lastName}` : (data.userName || 'Personnel'), 
                userPhoto: p ? p.photoUrl : null 
            }];
            expandedId.value = docSnap.id;
            loading.value = false;
            return;
        }
    }

    const qManagers = query(collection(db, 'userProfiles'), where('clientId', '==', cid), where('isDepartmentManager', '==', true), where('annualLeaveStatus', '==', true));
    const mSnap = await getDocs(qManagers);
    managersOnLeave.value = mSnap.docs.map(d => d.data());

    const q = query(collection(db, 'timesheetSubmissions'), where('clientId', '==', cid), where('status', '==', 'Pending Manager Approval'));
    const snap = await getDocs(q);
    const rawSubmissions = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    const myDepts = (profile?.departments || []).map(d => d.toLowerCase().trim());
    if (profile?.department) myDepts.push(profile.department.toLowerCase().trim());
    const uniqueMyDepts = [...new Set(myDepts)];

    const filtered = rawSubmissions.filter(sub => {
      if (userStore.isAdmin) return true;
      const subDept = getVal(sub, 'department')?.toLowerCase().trim();
      const amIManager = profile?.isDepartmentManager && uniqueMyDepts.includes(subDept);
      const amIDirector = profile?.isDepartmentDirector && uniqueMyDepts.includes(subDept);
      if (amIManager) return true;
      if (amIDirector) return managersOnLeave.value.some(m => (m.departments || []).map(d => d.toLowerCase().trim()).includes(subDept) || (m.department || '').toLowerCase().trim() === subDept);
      return false;
    });

    submissions.value = await Promise.all(filtered.map(async (sub) => {
      const pSnap = await getDoc(doc(db, 'userProfiles', sub.userId));
      const p = pSnap.exists() ? pSnap.data() : null;
      return { ...sub, userName: p ? `${p.firstName} ${p.lastName}` : (sub.userName || 'Personnel'), userPhoto: p ? p.photoUrl : null };
    }));

  } catch (err) { console.error(err); } finally { loading.value = false; }
};

const toggleExpand = (id) => { expandedId.value = expandedId.value === id ? null : id; };
const initialiseDecline = (sub) => { activeSubmission.value = sub; actionNotes.value = ''; showRejectionModal.value = true; };
const initialiseQuery = (sub) => { activeSubmission.value = sub; actionNotes.value = ''; showQueryModal.value = true; };
const closeModals = () => { showRejectionModal.value = false; showQueryModal.value = false; activeSubmission.value = null; actionNotes.value = ''; };

const initialiseVoid = async (sub) => {
    if (!confirm(`Confirm: VOID record ${sub.id}?`)) return;
    try {
        await updateDoc(doc(db, 'timesheetSubmissions', sub.id), { status: 'Voided', 'formData.status': 'Voided' });
        submissions.value = submissions.value.filter(s => s.id !== sub.id);
    } catch (err) { 
        console.error("Void Error:", err);
        alert(`Operational Failure: ${err.message || 'Action rejected.'}`);
    }
};

const confirmQuery = async () => {
    if (!actionNotes.value.trim() || !activeSubmission.value) return;
    isSubmittingAction.value = true;
    try {
        const sub = activeSubmission.value;
        const managerName = userStore.userProfile 
          ? `${userStore.userProfile.firstName || ''} ${userStore.userProfile.lastName || ''}`.trim() 
          : 'Manager';
        await updateDoc(doc(db, 'timesheetSubmissions', sub.id), { 
          status: 'Queried by Management', 
          'formData.status': 'Queried by Management', 
          queryNotes: actionNotes.value 
        });
        await purgeTimesheetNotification(sub.id);
        await addDoc(collection(db, 'user_actions'), { targetUserId: sub.userId, title: 'Timesheet Query', description: actionNotes.value, status: 'Pending', category: 'Finance', link: '/timesheets/history', createdAt: serverTimestamp(), createdBy: auth.currentUser.uid, creatorName: managerName });
        
        successMessage.value = `Query raised successfully for ${getVal(sub, 'userName') || 'Personnel'}.`;
        submissions.value = submissions.value.filter(s => s.id !== sub.id);
        closeModals();
        
        // 🛡️ RE-ROUTED: Taken back to Financial Command Centre when query completes in focused mode
        if (isFocusedMode.value) {
            setTimeout(() => { router.push({ name: 'AdminFinance' }); }, 1500);
        } else {
            setTimeout(() => { successMessage.value = ''; }, 3000);
        }
    } catch (err) { 
        console.error("Query Error:", err); 
        alert(`Operational Failure: ${err.message || 'Action rejected.'}`);
    } finally { isSubmittingAction.value = false; }
};

const confirmDecline = async () => {
    if (!actionNotes.value.trim() || !activeSubmission.value) return;
    isSubmittingAction.value = true;
    try {
        const sub = activeSubmission.value;
        const managerName = userStore.userProfile 
          ? `${userStore.userProfile.firstName || ''} ${userStore.userProfile.lastName || ''}`.trim() 
          : 'Manager';
        await updateDoc(doc(db, 'timesheetSubmissions', sub.id), { 
          status: 'Declined by Management', 
          'formData.status': 'Declined by Management', 
          rejectionNotes: actionNotes.value 
        });
        await purgeTimesheetNotification(sub.id);
        await addDoc(collection(db, 'user_actions'), { targetUserId: sub.userId, title: 'Timesheet Declined', description: actionNotes.value, status: 'Pending', category: 'Finance', link: '/timesheets/history', createdAt: serverTimestamp(), createdBy: auth.currentUser.uid, creatorName: managerName });
        
        successMessage.value = `Record for ${getVal(sub, 'userName') || 'Personnel'} declined successfully.`;
        submissions.value = submissions.value.filter(s => s.id !== sub.id);
        closeModals();
        
        // 🛡️ RE-ROUTED: Taken back to Financial Command Centre when decline completes in focused mode
        if (isFocusedMode.value) {
            setTimeout(() => { router.push({ name: 'AdminFinance' }); }, 1500);
        } else {
            setTimeout(() => { successMessage.value = ''; }, 3000);
        }
    } catch (err) { 
        console.error("Decline Error:", err); 
        alert(`Operational Failure: ${err.message || 'Action rejected.'}`);
    } finally { isSubmittingAction.value = false; }
};

const processAuthorisation = async (submission, newStatus) => {
  if (newStatus === 'Declined') return initialiseDecline(submission);
  if (!confirm(`Confirm Authorisation: Week ${getVal(submission, 'railWeek')}?`)) return;
  try {
    const managerName = userStore.userProfile 
      ? `${userStore.userProfile.firstName || ''} ${userStore.userProfile.lastName || ''}`.trim() 
      : 'Manager';
    await updateDoc(doc(db, 'timesheetSubmissions', submission.id), { 
      status: 'Authorised', 
      'formData.status': 'Authorised' 
    });
    await purgeTimesheetNotification(submission.id);
    await addDoc(collection(db, 'user_actions'), { targetUserId: submission.userId, title: 'Timesheet Authorised', description: `Week ${getVal(submission, 'railWeek')} is authorised.`, status: 'Pending', category: 'Finance', link: '/timesheets/history', createdAt: serverTimestamp(), createdBy: auth.currentUser.uid, creatorName: managerName });
    
    successMessage.value = `Record for ${getVal(submission, 'userName') || 'Personnel'} successfully Authorised.`;
    submissions.value = submissions.value.filter(s => s.id !== submission.id);
    
    // 🛡️ RE-ROUTED: Taken back to Financial Command Centre when authorisation completes in focused mode
    if (isFocusedMode.value) {
        setTimeout(() => { router.push({ name: 'AdminFinance' }); }, 1500);
    } else {
        setTimeout(() => { successMessage.value = ''; }, 3000);
    }
  } catch (err) { 
    console.error("Authorization Error:", err); 
    alert(`Operational Failure: ${err.message || 'Authorization rejected.'}`);
  }
};

watch(
  () => [userStore.isAuthReady, userStore.isUserProfileLoading],
  ([ready, loadingVal]) => {
    if (ready && !loadingVal) {
      fetchPendingLogs();
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.5s ease; }
.fade-slide-enter-from, .fade-slide-leave-to { opacity: 0; transform: translateY(-20px); }
</style>